import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';

export const TermsOfUse: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFF]">
      <Helmet>
        <title>Terms of Use – Cura AI Medical Assistant</title>
        <meta name="description" content="Review the terms and conditions for using Cura's AI-powered medical assistance service, including medical disclaimers and user responsibilities ✓" />
        <link rel="canonical" href="https://mycura.org/terms" />
        <meta property="og:title" content="Terms of Use – Cura AI Medical Assistant" />
        <meta property="og:description" content="Review the terms and conditions for using Cura's AI-powered medical assistance service, including medical disclaimers and user responsibilities." />
        <meta property="og:url" content="https://mycura.org/terms" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Use – Cura AI Medical Assistant" />
        <meta name="twitter:description" content="Review the terms and conditions for using Cura's AI-powered medical assistance service, including medical disclaimers and user responsibilities." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Cura Terms of Use",
            "url": "https://mycura.org/terms",
            "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
          })}
        </script>
      </Helmet>
      {/* Back Button - Top Left Corner */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Home</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200 py-16 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-bold mb-4 text-gray-900">Terms of Use</h1>
          <p className="text-xl text-gray-600">Important terms and conditions for using our service</p>
        </motion.div>
      </div>

      {/* Content Section */}
      <div className="py-16 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">


            <div className="space-y-12">
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Acceptance of Terms</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  By accessing and using our AI medical assistance service, you agree to be bound by these Terms of Use.
                  If you do not agree to these terms, please do not use our service.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Description</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Our AI medical assistance service provides general medical information and guidance. It is not a substitute for professional medical advice,
                  diagnosis, or treatment. Always seek the advice of qualified healthcare providers with questions regarding medical conditions.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Medical Disclaimer</h2>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <p className="text-gray-700 text-lg leading-relaxed">
                    The information provided by our AI service is for general informational purposes only. It should not be relied upon as a medical diagnosis
                    or treatment recommendation. In case of emergency, please contact emergency services or visit the nearest healthcare facility immediately.
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">User Responsibilities</h2>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <ul className="space-y-4 text-gray-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Provide accurate information when using the service</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Not use the service for emergency medical situations</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Maintain the confidentiality of your account information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Not misuse or attempt to exploit the service</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Report any security concerns or suspicious activities</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Intellectual Property</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  All content, features, and functionality of our service, including but not limited to text, graphics, logos, and AI algorithms,
                  are owned by us and protected by intellectual property laws.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Limitation of Liability</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  We are not liable for any damages arising from the use or inability to use our service. This includes but is not limited to direct,
                  indirect, incidental, consequential, and punitive damages.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Changes to Terms</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  We reserve the right to modify these terms at any time. We will notify users of any material changes.
                  Continued use of the service after changes constitutes acceptance of the modified terms.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Governing Law</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  These terms are governed by and construed in accordance with the laws of the United States.
                  Any disputes shall be subject to the exclusive jurisdiction of the courts in the United States.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Us</h2>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <p className="text-gray-700 text-lg mb-6">
                    For questions about these Terms of Use, please contact us:
                  </p>
                  <div className="space-y-4 text-gray-700">
                    <div className="flex items-center text-lg">
                      <span className="font-medium mr-2">Email:</span>
                      <a href="mailto:founders@mycura.org" className="text-blue-600 hover:text-blue-800 hover:underline">founders@mycura.org</a>
                    </div>
                    <div className="flex items-center text-lg">
                      <span className="font-medium mr-2">Phone:</span>
                      <a href="tel:+18665204146" className="text-blue-600 hover:text-blue-800 hover:underline">1-866-520-4146</a>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfUse; 